import React, { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TikTokShareConfirmCard — confirm card for
 *   `pending_action.type === "share_to_tiktok"`.
 *
 * Mirrors TweetConfirmCard + SnapShareConfirmCard so the user gets ONE
 * consistent confirm flow across all social platforms.
 *
 * Behavior:
 *  - TikTok's Content Posting API does the actual upload server-side
 *    (PULL_FROM_URL on aura.wwwwhynot.com). On tap, we hit
 *    `POST /api/tiktok/share/photo` with `{photo_id|wallpaper_id, title}`.
 *  - The photo lands in the user's TikTok DRAFTS. They open the TikTok
 *    app to add music + finalize the post.
 *
 * Props
 * -----
 *  - pending: { title_text, attachment_kind, attachment_id, wallpaper_id, char_count }
 *  - onSent / onCancelled: parent resolution callbacks.
 */
export function TikTokShareConfirmCard({ pending, onSent, onCancelled }) {
  const [text, setText] = useState(pending?.title_text || "");
  const [posting, setPosting] = useState(false);
  const [status, setStatus] = useState(null); // 'sent' | 'cancelled' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const charCount = text.length;
  const overLimit = charCount > 140;

  const attachmentKind = pending?.attachment_kind || (pending?.wallpaper_id ? "wallpaper" : null);
  const attachmentId = pending?.attachment_id || pending?.wallpaper_id || null;

  if (status === "sent") {
    return (
      <div className="tweet-confirm-card sent" data-testid="tiktok-confirm-card-sent">
        <span>✓ Sent to your TikTok drafts. Open the TikTok app to add music + post.</span>
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="tweet-confirm-card cancelled" data-testid="tiktok-confirm-card-cancelled">
        ↩ TikTok cancelled — nothing posted.
      </div>
    );
  }

  return (
    <div className="tweet-confirm-card tiktok-confirm-card" data-testid="tiktok-confirm-card">
      <div className="tweet-confirm-header">
        <span className="tweet-confirm-bird" aria-hidden="true">🎵</span>
        <span className="tweet-confirm-label">Drop into TikTok drafts</span>
        <span className={`tweet-confirm-count ${overLimit ? "over" : ""}`}>
          {charCount}/140
        </span>
      </div>
      <textarea
        className="tweet-confirm-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={160}
        data-testid="tiktok-confirm-text"
        placeholder="TikTok title — keep it hook-first"
      />
      {errorMsg && <div className="tweet-confirm-error" data-testid="tiktok-confirm-error">{errorMsg}</div>}

      <div className="tweet-confirm-actions">
        <button
          className="tweet-confirm-cancel"
          onClick={() => { setStatus("cancelled"); onCancelled?.(); }}
          disabled={posting}
          data-testid="tiktok-confirm-cancel"
        >
          Cancel
        </button>
        <button
          className="tweet-confirm-send"
          disabled={posting || overLimit || !attachmentId}
          data-testid="tiktok-confirm-send"
          onClick={async () => {
            setPosting(true);
            setErrorMsg("");
            try {
              const payload = attachmentKind === "wallpaper"
                ? { wallpaper_id: attachmentId, title: text || undefined }
                : { photo_id: attachmentId,     title: text || undefined };
              await axios.post(`${API}/tiktok/share/photo`, payload);
              setStatus("sent");
              onSent?.();
            } catch (e) {
              const detail = e?.response?.data?.detail || "TikTok didn't accept the post — try again?";
              setErrorMsg(detail);
            } finally {
              setPosting(false);
            }
          }}
        >
          {posting ? "Sending to drafts…" : "Send to TikTok drafts"}
        </button>
      </div>
    </div>
  );
}

export default TikTokShareConfirmCard;
